import React, { useEffect, useState, useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import Button from "@mui/material//Button";
import TextField from "@mui/material//TextField";
import Link from "@mui/material//Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { emailValidation, requiredPasswordValidation } from "../../Utils/Custom/validations";
import { pathRoute } from "../../Utils/Routes/routes";
import Copyright from "../../Utils/Custom/Copyright";
import * as actions from "../../../store/actions";
import { PathStateObject } from "../../Utils/Models/routesModel";

interface Props {
  signInUser?: (email: string, password: string) => void;
  isSigningIn?: boolean;
  signInErrorCode?: string;
  signOutErrorCode?: string;
  isAuthenticated?: boolean;
  isEmailVerified?: boolean;
  signOutUser?: () => void;
  sendVerificationEmail?: () => void;
  isSendVerificationEmailSuccess?: boolean;
  sendVerificationEmailErrorCode?: string;
  isSendVerificationEmailRequesting?: boolean;
}

const SignIn = ({
  signInUser,
  isSigningIn,
  signInErrorCode,
  signOutErrorCode,
  isAuthenticated,
  isEmailVerified,
  signOutUser,
  sendVerificationEmail,
  isSendVerificationEmailSuccess,
  sendVerificationEmailErrorCode,
  isSendVerificationEmailRequesting,
}: Props) => {
  const navigate = useNavigate();

  const theme = useTheme();

  const loading = isSigningIn || isSendVerificationEmailRequesting;

  const [showPassword, setShowPassword] = useState(false);

  const location: any = useLocation();

  const pathStateObject: PathStateObject =
    location.state ||
    ({
      from: pathRoute.Dashboard,
      message: "",
    } as PathStateObject);

  useEffect(() => {
    if (pathStateObject.message === "InvitedUserCreated") {
      toast.success("Saved");
    }
  }, []);

  const signInWithEmailPassword = async (values: any) => {
    signInUser && signInUser(values?.email as any, values?.password as any);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(pathRoute.Dashboard, {
        replace: true,
      });
    }
  }, [isEmailVerified, isAuthenticated]);

  useEffect(() => {
    if (signInErrorCode && signInErrorCode.length > 0) {
      //https://firebase.google.com/docs/reference/js/firebase.auth.Auth#signinwithemailandpassword
      switch (signInErrorCode) {
        case "auth/invalid-email":
          toast.error("Error: Invalid email");
          break;
        case "auth/wrong-password":
          toast.error("Error: Incorrect password");
          break;
        case "auth/user-not-found":
          toast.error(
            "Error: User not found. This can occur if the user is deleted in between the time when the link was issued and the page was called"
          );
          break;
        case "auth/user-disabled":
          toast.error("Error: The user has been disabled");
          break;
        case "auth/weak-password":
          toast.error("Error: The new password is not strong enough");
          break;
        default:
          toast.error("Error: Incorrect password");
          break;
      }
      formik.resetForm();
    }
  }, [signInErrorCode]);

  useEffect(() => {
    if (signOutErrorCode && signOutErrorCode.length > 0) {
      //https://firebase.google.com/docs/reference/js/firebase.auth.Auth#signout
      toast.error("Error");
    }
  }, [signOutErrorCode]);

  useEffect(() => {
    if (isSendVerificationEmailSuccess) {
      toast.success("Email Sent");
      formik.resetForm();
    }
  }, [isSendVerificationEmailSuccess]);

  useEffect(() => {
    if (sendVerificationEmailErrorCode && sendVerificationEmailErrorCode.length > 0) {
      formik.resetForm();
    }
  }, [sendVerificationEmailErrorCode]);

  const validate = (values: { email: string; password: string }) => {
    const errors: any = {};
    if (emailValidation(values.email)) {
      errors.email = emailValidation(values.email);
    }
    if (requiredPasswordValidation(values.password)) {
      errors.password = requiredPasswordValidation(values.password);
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validate,
    onSubmit: async (values: any) => {
      await signInWithEmailPassword(values);
    },
    onReset: (values: any) => {
      //
    },
  });

  let emptyForm = true;
  if (formik.values.email && formik.values.password) {
    emptyForm = false;
  }

  const classes = useMemo(
    () => ({
      whiteText: {
        color: theme.palette.text.primary,
      },
      cardBackgroud: {
        backgroundColor: theme.palette.background.paper,
      },
      VisibilityIcon: {
        color: "white",
      },
    }),
    []
  );

  return (
    <Container component="main" maxWidth="xs">
      <div>
        <Card sx={{ mt: 15 }} style={classes.cardBackgroud}>
          <CardContent>
            <Grid container flexDirection="column" alignItems="center" sx={{ my: 2 }}>
              <img
                src={
                  theme.palette.mode === "light"
                    ? "/images/anavio-logo-light.svg"
                    : "/images/anavio-logo.svg"
                }
                alt="Anavio logo"
                style={{ width: "200px" }}
              />
              <Typography
                component="h1"
                variant="h5"
                color="primary"
                sx={{ mt: 2 }}
                style={classes.whiteText}
              >
                Sign In
              </Typography>
            </Grid>
            <div>
              <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
                <TextField
                  autoComplete="email"
                  size="small"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  id="email"
                  label="Email"
                  {...formik.getFieldProps("email")}
                  disabled={formik.isSubmitting}
                  error={formik.touched.email && formik.errors.email ? true : false}
                  helperText={
                    formik.touched.email && formik.errors.email ? formik.errors.email : ""
                  }
                />
                <TextField
                  autoComplete="current-password"
                  size="small"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  {...formik.getFieldProps("password")}
                  disabled={formik.isSubmitting}
                  error={formik.touched.password && formik.errors.password ? true : false}
                  helperText={
                    formik.touched.password && formik.errors.password ? formik.errors.password : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                            event.preventDefault()
                          }
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityIcon style={classes.VisibilityIcon} />
                          ) : (
                            <VisibilityOffIcon style={classes.VisibilityIcon} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  sx={{ mt: 2 }}
                  style={{ opacity: formik.isSubmitting || emptyForm ? 0.4 : 1 }}
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disableElevation
                >
                  Sign In
                </Button>
                <Grid container sx={{ mt: 2 }}>
                  <Grid item xs>
                    <Link href={pathRoute.SignIn.ForgotPassword} variant="body2">
                      Forgot password?
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </div>
            {loading ? <LinearProgress /> : null}
          </CardContent>
        </Card>
        <Box mt={2}>
          <Copyright />
        </Box>
      </div>
    </Container>
  );
};
const mapStateToProps = (state: any /*, ownProps*/) => {
  return {
    isSigningIn: state.auth.isSigningIn,
    signInErrorCode: state.auth.signInErrorCode,
    signOutErrorCode: state.auth.signOutErrorCode,
    isAuthenticated: state.auth.isAuthenticated,
    isEmailVerified: state.auth.isEmailVerified,
    isSendVerificationEmailSuccess: state.auth.isSendVerificationEmailSuccess,
    sendVerificationEmailErrorCode: state.auth.sendVerificationEmailErrorCode,
    isSendVerificationEmailRequesting: state.auth.isSendVerificationEmailRequesting,
  };
};
function mapDispatchToProps(dispatch: any) {
  return {
    signInUser: (email: string, password: string) => dispatch(actions.signInUser(email, password)),
    signOutUser: () => dispatch(actions.signOutUser()),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
